import React, { useState } from 'react';
import { X, FileText, ChevronDown, ChevronRight } from 'lucide-react';

const LeadSubstatusCheatsheet = ({ isOpen, onClose }) => {
  const [expandedStatus, setExpandedStatus] = useState('Lead Received');

  const substatuses = {
    'Lead Received': [
      { 
        name: 'Initial Message Sent (Unopened)', 
        icon: 'leademail.png', 
        description: 'First message sent but not opened yet',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      { 
        name: 'Initial Message Sent (Opened)', 
        icon: 'leademailopened.png', 
        description: 'First message sent and opened by client',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      { 
        name: 'First Drip Message Sent', 
        icon: 'leademailtwo.png', 
        description: 'Second follow-up message sent',
        color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      },
      { 
        name: 'First Drip Message Sent (Client Opened)', 
        icon: 'leademailtwoopened.png', 
        description: 'Second follow-up message opened by client',
        color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      },
      { 
        name: 'Second Drip Message Sent', 
        icon: 'leademailthree.png', 
        description: 'Second drip follow-up message sent',
        color: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
      },
      { 
        name: 'Second Drip Message Sent (Opened)', 
        icon: 'leademailthreeopened.png', 
        description: 'Second drip follow-up message opened by client',
        color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      },
      { 
        name: 'Final Message Sent', 
        icon: 'leademaillast.png', 
        description: 'Final follow-up message sent',
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      },
      { 
        name: 'Final Message Sent (Opened)', 
        icon: 'leademaillastopened.png', 
        description: 'Final follow-up message opened by client',
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      },
      { 
        name: 'Cold Lead', 
        icon: 'coldlead.svg', 
        description: 'Lead that has not responded to any messages',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    ],
    'Discovery Call': [
      { 
        name: 'Scheduled (Confirmed)', 
        icon: 'scheduled.svg', 
        description: 'Discovery call scheduled and confirmed',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      { 
        name: 'Scheduled (Not Confirmed)', 
        icon: 'schedulednoconfirm.svg', 
        description: 'Discovery call scheduled but not yet confirmed',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      { 
        name: 'Ghosted', 
        icon: 'ghost.svg', 
        description: 'Lead has stopped responding to communications',
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      },
      { 
        name: 'Rescheduled (Not Confirmed)', 
        icon: 'rescheduled.svg', 
        description: 'Discovery call rescheduled but not yet confirmed',
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      },
      { 
        name: 'Rescheduled (Confirmed)', 
        icon: 'rescheduledconfirm.svg', 
        description: 'Discovery call rescheduled and confirmed',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      { 
        name: 'Cold Lead', 
        icon: 'coldlead.svg', 
        description: 'Lead that has not responded to any communications',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    ],
    'Assessment': [
      { 
        name: 'Portal Invite Sent', 
        icon: 'invitesent.svg', 
        description: 'Assessment portal invite link has been created',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      { 
        name: 'Signed Up For Portal', 
        icon: 'portalsignup.png', 
        description: 'Lead has signed up for the portal',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      { 
        name: 'NDA Signed', 
        icon: 'ndadone.png', 
        description: 'Lead has signed the NDA',
        color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      },
      { 
        name: 'Assessment Done', 
        icon: 'assesdone.png', 
        description: 'Lead has completed the assessment',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      { 
        name: 'Solutions Chosen', 
        icon: 'solutionsdone.png', 
        description: 'Lead has chosen their solutions',
        color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
      },
      { 
        name: 'Cold Lead', 
        icon: 'coldlead.svg', 
        description: 'Lead that has not responded to any communications',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    ],
    'Onboarding Call': [
      { 
        name: 'Scheduled (Confirmed)', 
        icon: 'scheduled.svg', 
        description: 'Onboarding call scheduled and confirmed',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      },
      { 
        name: 'Scheduled (Not Confirmed)', 
        icon: 'schedulednoconfirm.svg', 
        description: 'Onboarding call scheduled but not yet confirmed',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      { 
        name: 'Ghosted', 
        icon: 'ghost.svg', 
        description: 'Lead has stopped responding to communications',
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      },
      { 
        name: 'Rescheduled (Not Confirmed)', 
        icon: 'rescheduled.svg', 
        description: 'Onboarding call rescheduled but not yet confirmed',
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      },
      { 
        name: 'Rescheduled (Confirmed)', 
        icon: 'rescheduledconfirm.svg', 
        description: 'Onboarding call rescheduled and confirmed',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      { 
        name: 'Cold Lead', 
        icon: 'coldlead.svg', 
        description: 'Lead that has not responded to any communications',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      }
    ],
    'Proposal': [
      { 
        name: 'Not Started', 
        icon: 'nostart.svg', 
        description: 'Proposal has not been started yet',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      },
      { 
        name: 'Draft', 
        icon: 'draft.svg', 
        description: 'Proposal is in draft stage',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      { 
        name: 'Pending Payment', 
        icon: 'hourglass.svg', 
        description: 'Proposal sent and waiting for payment',
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      }
    ],
    'Won': [
      { 
        name: 'Won', 
        icon: 'won.svg', 
        description: 'Lead has been converted to a client',
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      }
    ],
    'Lost': [
      { 
        name: 'Lost', 
        icon: 'lost.svg', 
        description: 'Lead has been lost',
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      }
    ]
  };

  const toggleStatus = (status) => {
    setExpandedStatus(expandedStatus === status ? null : status);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1"></div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Lead Substatus Cheatsheet</h2>
          <div className="flex-1 flex justify-end">
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {Object.entries(substatuses).map(([status, statusSubstatuses]) => (
              <div key={status} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <button
                  onClick={() => toggleStatus(status)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{status}</h3>
                  {expandedStatus === status ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                
                {expandedStatus === status && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {statusSubstatuses.map((substatus, index) => (
                        <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex-shrink-0 w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                            <img 
                              src={process.env.PUBLIC_URL + '/' + substatus.icon} 
                              alt={substatus.name}
                              className="w-5 h-5 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <div className="w-5 h-5 bg-gray-400 rounded hidden"></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-1 ${substatus.color}`}>
                              {substatus.name}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {substatus.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>


      </div>
    </div>
  );
};

export default LeadSubstatusCheatsheet; 